import { toast } from "react-toastify";

export const CLIPBOARD_TOAST_CONTAINER_ID = "clipboard-feedback";

type ClipboardFeedbackMode = "toast" | "alert" | "none";

interface ClipboardOptions {
  feedbackMode?: ClipboardFeedbackMode;
  successMessage?: string;
  errorMessage?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

const showToastFeedback = (type: "success" | "error", message: string) => {
  toast[type](message, {
    position: "top-center",
    autoClose: 2200,
    hideProgressBar: true,
    closeButton: false,
  });
};

const fallbackCopyText = (text: string): boolean => {
  if (typeof document === "undefined") {
    return false;
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  textArea.style.pointerEvents = "none";

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  let copied = false;

  try {
    copied = document.execCommand("copy");
  } finally {
    document.body.removeChild(textArea);
  }

  return copied;
};

export const handleCopyClipBoard = async (
  text: string,
  {
    feedbackMode = "toast",
    successMessage = "클립보드에 링크가 복사되었어요.",
    errorMessage = "클립보드 복사에 실패했어요.",
    onSuccess,
    onError,
  }: ClipboardOptions = {},
): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const copied = fallbackCopyText(text);

      if (!copied) {
        throw new Error("Clipboard copy is not supported in this browser.");
      }
    }

    if (successMessage) {
      if (feedbackMode === "alert") {
        toast.error(successMessage);
      }

      if (feedbackMode === "toast") {
        showToastFeedback("success", successMessage);
      }
    }

    onSuccess?.();
    return true;
  } catch (err) {
    const error = err instanceof Error ? err : new Error("클립보드 복사에 실패했습니다.");

    console.error(error);

    if (errorMessage) {
      if (feedbackMode === "alert") {
        toast.error(errorMessage);
      }

      if (feedbackMode === "toast") {
        showToastFeedback("error", errorMessage);
      }
    }

    onError?.(error);
    return false;
  }
};
